import { Link, Typography } from '@mui/material';
import { PersonContext } from 'Context/CurrentPerson';
import useTranslate from 'Hooks/useTranslate';
import { PersonAvatar } from 'Theme/components/Avatar';
import { userFullName } from 'Tools';
import Account from './Account';
import Administrators from './Administrators';
import Directories from './Directories';
import LastActivities from './LastActivities';
import PersonalInformationContainer from './PersonalInformation/PersonalInformation.container';
import { useStyles } from './TabGeneral.styles';
import TechnicalInformation from './TechnicalInformation/TechnicalInformation';
import { ActivationStatus } from 'Apollo';
import AlertDisabledAccount from 'Components/AlertDisabledAccount';
import { useContext, useState } from 'react';
import ModalDataPrivacy from 'Components/ModalDataPrivacy/ModalDataPrivacy';
import { CdsColorActionNeutralActive } from '@cegid/design-tokens';
import { displayDefaultColor } from 'Tools/displayDefaultColor';
import RemoveProfileLink from './PersonalInformation/RemoveProfile/RemoveProfileLink';

interface Props {
  person: IPersonDetailed;
}

function TabGeneral({person}: Readonly<Props>) {
  const classes = useStyles();
  const ts = useTranslate();
  const { currentPersonRoles, currentPerson } = useContext(PersonContext);

  const isDisabled = person?.status === ActivationStatus.Disabled;
  const isNotActivatedOrNoInitial = displayDefaultColor(person);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

  const isCurrentUser = currentPerson.immutableId === person.immutableId;
  const isOrganizational = person.organization !== null;

  // should the edit ui be displayed?
  // the fine grained access right are handled in the backend
  const canManageProfile =
    (!isOrganizational && isCurrentUser) ||
    (isOrganizational && currentPersonRoles.isOrganizationOwner) ||
    currentPersonRoles.isAccountAdmin ||
    currentPersonRoles.canWriteAll;

  return (
    <>
      {isDisabled && <AlertDisabledAccount />}
      <div className={classes.header}>
        <PersonAvatar
          component="span"
          person={person}
          size="larger"
          className={classes.headerAvatar}
          isDisabled={isNotActivatedOrNoInitial}
        />

        {isCurrentUser && (
          <Typography variant="h5" color="textSecondary">
            {ts('common.welcome')}
          </Typography>
        )}
        <Typography variant="h5">
          {userFullName(person.firstName, person.lastName)}
        </Typography>
      </div>
      <PersonalInformationContainer person={person} />
      <Account person={person} canManageProfile={canManageProfile} isCurrentUser={isCurrentUser} isOrganizationAdmin={currentPersonRoles.isOrganizationOwner} organizationCode={person.organization?.code ?? undefined} />
      {!isDisabled && !!person.account && <Administrators person={person} />}
      {!isDisabled && <Directories person={person} organizationCode={person.organization?.code ?? undefined} />}
      <LastActivities person={person} />
      {currentPersonRoles.hasAccessToAccounts && (
        <TechnicalInformation person={person} />
      )}
      {isCurrentUser && (
        <Typography variant="caption" color="textSecondary" paragraph textAlign={'justify'}>
          {ts('dataPrivacy.userProfileDataPrivacy')}{' '}
          <Link 
            color={CdsColorActionNeutralActive}
            type="button"
            aria-label={ts('common.action.see.seeMore')}
            underline='hover'
            onClick={()=> setModalIsOpen(true)} 
            style={{cursor: 'pointer'}}
          >
            {ts('common.action.see.seeMore')}...
          </Link>
        </Typography>
      )}
      {isCurrentUser && !isOrganizational && (
        <RemoveProfileLink person={person} />
      )}
      {modalIsOpen && (
        <ModalDataPrivacy
          isOpen={modalIsOpen}
          onClose={() => {
            setModalIsOpen(false);
          }}
        />
      )}
    </>
  );
}

export default TabGeneral;
